import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import firebase from "../../config/firebase";
import freightApi2 from "../../service/modules/freight.api";
import userService from "../../service/user.service";
import {
  buildLancesBadgeTooltip,
  extractAvailableFreightPayerName,
  getUnseenLancesFreightIds,
} from "./lancesNewFreightsBadge";
import { isAnonymousShipperLabel } from "./carrierPriceVisibility";
import {
  HUB_TAB_AVAILABLE,
  buildCarrierHubParams,
  mapCarrierHubListItem,
  parseCarrierHubResponse,
} from "../../view/lances/carrierHub.mapper";
import { EMPTY_FILTERS } from "../../view/lances/mockData";

require("firebase/auth");

const POLL_INTERVAL_MS = 60000;
const BADGE_FETCH_LIMIT = 100;

function asNonEmptyName(value) {
  if (value == null) return "";
  const text = String(value).trim();
  if (!text || text === "—" || text === "-") return "";
  if (isAnonymousShipperLabel(text)) return "";
  return text;
}

async function resolveShipperNamesByIds(shipperIds = []) {
  const uniqueIds = Array.from(
    new Set(
      (shipperIds || []).map((id) => String(id || "").trim()).filter(Boolean),
    ),
  );
  if (!uniqueIds.length) return {};

  const docs = await Promise.all(
    uniqueIds.map((id) =>
      firebase.firestore().collection("shipper").doc(id).get(),
    ),
  );

  const byId = {};
  docs.forEach((doc) => {
    if (!doc.exists) return;
    const data = doc.data() || {};
    const personal = data.dataPersonal || {};
    const name =
      asNonEmptyName(personal.nameFantasy) ||
      asNonEmptyName(personal.socialName) ||
      asNonEmptyName(data.nameFantasy) ||
      asNonEmptyName(data.socialName) ||
      "";
    if (name) byId[doc.id] = name;
  });

  return byId;
}

/**
 * Conta fretes disponíveis novos (hub tab=available, onlyMyShippers).
 * Badge + tooltip no menu Lances (prioriza nome do pagador).
 */
export default function useLancesNewFreightsBadge(user, enabled = true) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [tooltipTitle, setTooltipTitle] = useState("");
  const pollRequestRef = useRef(0);

  const viewerKey = useMemo(
    () => String(user?.uid || user?.uidShipper || "").trim(),
    [user?.uid, user?.uidShipper],
  );

  const canPoll = Boolean(
    enabled &&
      viewerKey &&
      user?.uidShipper &&
      user?.permissoes?.lances?.isView,
  );

  const refreshCount = useCallback(async () => {
    if (!canPoll) {
      setUnreadCount(0);
      setTooltipTitle("");
      return;
    }

    const requestId = ++pollRequestRef.current;
    const carrierId = String(user?.uidShipper || "").trim();

    try {
      const response = await freightApi2.getCarrierHub(
        buildCarrierHubParams(
          { ...EMPTY_FILTERS, onlyMyShippers: true },
          {
            tab: HUB_TAB_AVAILABLE,
            idCarrier: carrierId,
            idUser: String(user?.uid || "").trim() || undefined,
            limit: BADGE_FETCH_LIMIT,
          },
        ),
      );

      if (requestId !== pollRequestRef.current) return;

      const viewerShipperIds = userService.getShipperIdsFromUser(user);
      const parsed = parseCarrierHubResponse(response);

      const candidates = (parsed.items || [])
        .map((item) => ({
          raw: item,
          mapped: mapCarrierHubListItem(item, { viewerShipperIds }),
        }))
        .filter(({ mapped }) => mapped?.id)
        .filter(({ mapped }) => mapped.isMyClient === true || mapped.isAffiliateShipper === true);

      // Se o hub não marcar isMyClient, ainda assim usa onlyMyShippers=true no request.
      const list =
        candidates.length > 0
          ? candidates
          : (parsed.items || [])
              .map((item) => ({
                raw: item,
                mapped: mapCarrierHubListItem(item, { viewerShipperIds }),
              }))
              .filter(({ mapped }) => mapped?.id);

      const unseenIds = new Set(
        getUnseenLancesFreightIds(
          viewerKey,
          list.map(({ mapped }) => mapped.id),
        ),
      );

      const unseenCandidates = list.filter(({ mapped }) =>
        unseenIds.has(String(mapped.id)),
      );

      const namesFromApi = [];
      const idsNeedingLookup = [];

      unseenCandidates.forEach(({ raw, mapped }) => {
        const fromRaw = asNonEmptyName(extractAvailableFreightPayerName(raw));
        const fromMapped = asNonEmptyName(mapped.shipper);
        const name = fromRaw || fromMapped;
        if (name) {
          namesFromApi.push(name);
          return;
        }
        const shipperId = String(mapped.idShipper || "").trim();
        if (shipperId) idsNeedingLookup.push(shipperId);
      });

      let resolvedNames = [];
      if (idsNeedingLookup.length) {
        const byId = await resolveShipperNamesByIds(idsNeedingLookup);
        if (requestId !== pollRequestRef.current) return;
        resolvedNames = idsNeedingLookup
          .map((id) => byId[id] || "")
          .filter(Boolean);
      }

      const payerNames = [...namesFromApi, ...resolvedNames];

      setUnreadCount(unseenCandidates.length);
      setTooltipTitle(buildLancesBadgeTooltip(payerNames));
    } catch (error) {
      // Fallback legado: se o hub falhar, não derruba o menu.
      console.error("[useLancesNewFreightsBadge] Erro ao atualizar badge:", error);
    }
  }, [canPoll, user, viewerKey]);

  useEffect(() => {
    if (!canPoll) {
      setUnreadCount(0);
      setTooltipTitle("");
      return undefined;
    }

    refreshCount();
    const intervalId = window.setInterval(refreshCount, POLL_INTERVAL_MS);

    const onSeenUpdated = (event) => {
      if (event?.detail?.viewerKey && event.detail.viewerKey !== viewerKey) {
        return;
      }
      refreshCount();
    };

    window.addEventListener("lances-seen-freights-updated", onSeenUpdated);
    window.addEventListener("focus", refreshCount);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("lances-seen-freights-updated", onSeenUpdated);
      window.removeEventListener("focus", refreshCount);
    };
  }, [canPoll, refreshCount, viewerKey]);

  return { unreadCount, tooltipTitle };
}
